<script setup>
/**
 * FormControlVariationsOption
 * A form control for selecting product variations.
 * It displays variation options as a set of buttons, allowing for single or multiple selections.
 * Supports shift-clicking for range selection and a "select all" functionality by clicking the form label.
 */
import {ref, computed, onMounted} from 'vue'
import {useRoute} from 'vue-router'
import api from '@/services/api'
import {useFormStore} from '@/stores/form'

const props = defineProps({
    field: Object,
    formId: String,
    currentData: Object,
})

// Composables & Dynamic Pinia Store
const route = useRoute()
const formStore = computed(() => props.formId ? useFormStore(props.formId) : null)

const lastClickedVariationId = ref(undefined)
const items = ref([])
const container = ref(null)

const selectedVariableIds = computed(() => props.currentData[props.field.name])
const inputValue = computed(() => (selectedVariableIds.value.length ? '1' : ''))
const tableName = computed(() => formStore.value?.tableName)

const titles = computed(() => {
    const titles = {}
    if (items.value) {
        items.value.forEach((item) => {
            titles[item.id] = item.title
        })
    }
    return titles
})

const variableIds = computed(() => {
    const variableIds = []
    if (items.value) {
        items.value.forEach((item) => {
            variableIds.push(item.id)
        })
    }
    return variableIds
})

const fetchItems = async () => {
    if (route.query.productId) {
        try {
            const obj1 = await api.post('api/products/get-type-from-table-name', {
                tableName: tableName.value,
            })
            const typeId = obj1.data.id

            const obj2 = await api.post('api/products/get-types-column-names', {
                typeId,
            })

            let optionId
            for (const _optionId in obj2.data.columnNames) {
                if (Object.prototype.hasOwnProperty.call(obj2.data.columnNames, _optionId)) {
                    const columnName = obj2.data.columnNames[_optionId]
                    if (columnName === camelToSnakeCase(props.field.name)) {
                        optionId = _optionId
                    }
                }
            }

            if (optionId) {
                const obj3 = await api.get('api/component/product_variables', {
                    params: {
                        optionId: optionId,
                        sort: 'displayOrder',
                    },
                })
                items.value = obj3.data.items
            }
        } catch (error) {
            console.error('Error fetching items:', error)
            items.value = []
        }
    }
}

// Pinia store action call for default values
formStore.value?.setDefaultValue({
    name: props.field.name,
    value: [],
})

const assignSelectAllEventToFormLabels = () => {
    const label = container.value.closest('[data-form-group]')?.getElementsByTagName('label')[0]
    if (label) {
        label.addEventListener('click', () => {
            if (selectedVariableIds.value.length === variableIds.value.length) {
                selectedVariableIds.value.splice(0)
            } else {
                const newIds = variableIds.value.filter(id => !selectedVariableIds.value.includes(id))
                selectedVariableIds.value.push(...newIds)
            }
        })
    }
}

onMounted(() => {
    fetchItems()
    assignSelectAllEventToFormLabels()
})

const selectOption = (e, variableId) => {
    if (e.shiftKey && lastClickedVariationId.value !== undefined && lastClickedVariationId.value !== variableId) {
        const index1 = variableIds.value.indexOf(variableId)
        const index2 = variableIds.value.indexOf(lastClickedVariationId.value)
        const start = Math.min(index1, index2)
        const end = Math.max(index1, index2)

        for (let i = start; i <= end; i++) {
            const varId = variableIds.value[i]
            if (!props.currentData[props.field.name].includes(varId)) {
                props.currentData[props.field.name].push(varId)
            }
        }
    } else {
        const i = props.currentData[props.field.name].indexOf(variableId)
        if (i === -1) {
            props.currentData[props.field.name].push(variableId)
        } else {
            props.currentData[props.field.name].splice(i, 1)
        }
    }

    lastClickedVariationId.value = variableId
}

const camelToSnakeCase = (str) => {
    return str
        .replace(/[A-Z]/g, '_$&')
        .toLowerCase()
        .replace(/^_+/, '')
}

</script>

<template>
    <div ref="container">
        <!--
          This hidden field is used to apply "required" validation to the buttons. When one or more buttons are
          selected the input is populated, and so the field passes the required validation.
          Required fields cannot be hidden, otherwise the browser's built in validation fails and generates a "An
          invalid form control with name='' is not focusable." error. So using the following style fixes it.
        -->
        <input v-model="inputValue" :name="field.name" required style="" tabindex="-1">

        <button v-for="variableId in variableIds" :key="variableId"
                :class="{ 'active': selectedVariableIds.indexOf(variableId) !== -1 }"
                class="btn btn-light btn-sm m-1 ms-0 mt-0" type="button" @click="selectOption($event, variableId)">
            {{ titles[variableId] }}
        </button>
    </div>
</template>

<style scoped>
input {
    position: absolute;
    z-index: -1;
    opacity: 0;
    width: 0;
}
</style>